import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { BellRing, Trash2 } from "lucide-react";

function labelFor(f) {
  const bits = [];
  if (f.bhk) bits.push(`${f.bhk} BHK`);
  bits.push(f.property_type || "properties");
  if (f.listing_type === "rent") bits.push("to rent");
  else if (f.listing_type === "sale") bits.push("for sale");
  const loc = [f.locality, f.city].filter(Boolean).join(", ");
  if (loc) bits.push(`in ${loc}`);
  return bits.join(" ");
}

/**
 * Shows a logged-in user's saved search alerts on their dashboard, with a
 * one-click unsubscribe. Renders nothing if the user has none saved.
 */
export default function SavedSearchesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    api.get("/saved-searches/mine").then(({ data }) => setItems(data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/saved-searches/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Alert removed");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(null);
    }
  };

  if (loading || items.length === 0) return null;

  return (
    <div className="bg-white border border-brand-subtle rounded-2xl p-6 mb-6" data-testid="saved-searches-panel">
      <p className="label-eyebrow text-gray-500 mb-4 flex items-center gap-1.5">
        <BellRing size={13} className="brand-forest" /> Your saved search alerts
      </p>
      <div className="space-y-2">
        {items.map((s) => (
          <div key={s.id} data-testid={`saved-search-${s.id}`} className="flex items-center justify-between gap-3 bg-[#FBF6EC] rounded-xl px-4 py-3">
            <p className="text-sm text-gray-700 capitalize">{labelFor(s.filters)}</p>
            <button
              onClick={() => remove(s.id)}
              disabled={busy === s.id}
              data-testid={`saved-search-delete-${s.id}`}
              className="text-gray-400 hover:text-red-500 shrink-0"
              aria-label="Remove alert"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
